import {
  getProcessingSemantic,
  getProcessingStatusColor,
  getReviewSemantic,
  getReviewStatusColor,
} from "@/lib/semanticColors";

/**
 * Every column the API can return empty renders this rather than collapsing to
 * a blank cell. The old app spelled it "N/A" via `getValueOrNA`; this app uses
 * an em dash for the same job everywhere else, so it keeps the one convention.
 */
export const EMPTY_VALUE = "—";

export function formatValueOrEmpty(value) {
  if (value == null) return EMPTY_VALUE;
  if (typeof value === "string" && value.trim() === "") return EMPTY_VALUE;
  return value;
}

/**
 * What to call an invoice in a sentence: its number, else its supplier, else a
 * plain "This invoice". An invoice can have no number yet (extraction not done),
 * and printing the missing value gives "null".
 */
export function getInvoiceLabel(invoice) {
  const named = [invoice?.invoiceNumber, invoice?.companyName].find(
    (value) => typeof value === "string" && value.trim() !== "",
  );
  return named?.trim() ?? "This invoice";
}

/** The review half, on its own. */
export function getReviewStatusDisplay(invoice) {
  return {
    label: invoice?.reviewStatus ?? EMPTY_VALUE,
    color: getReviewStatusColor(invoice?.reviewStatusId),
    semantic: getReviewSemantic(invoice?.reviewStatusId),
    tooltip: `Review status: ${invoice?.reviewStatus ?? "unknown"}`,
  };
}

/** The processing half, on its own. */
export function getProcessingStatusDisplay(invoice) {
  return {
    label: invoice?.processingStatus ?? EMPTY_VALUE,
    color: getProcessingStatusColor(invoice?.processingStatusId),
    semantic: getProcessingSemantic(invoice?.processingStatusId),
    tooltip: `Processing status: ${invoice?.processingStatus ?? "unknown"}`,
  };
}

/**
 * The combined badge: whichever status is the live one for this invoice.
 *
 * Under review, the review status is what is moving and the processing status
 * is just "Under Review"; otherwise the processing status is the news. The
 * queue tables now show both in their own columns, so this is only used where
 * there is room for a single badge — the detail header.
 */
export function getInvoiceStatusDisplay(invoice) {
  return invoice?.processingStatusId === 2
    ? {
        ...getReviewStatusDisplay(invoice),
        tooltip: `Processing Status: ${invoice?.processingStatus ?? "unknown"}`,
      }
    : {
        ...getProcessingStatusDisplay(invoice),
        tooltip: `Review Status: ${invoice?.reviewStatus ?? "unknown"}`,
      };
}

const STATUS_DISPLAY = {
  auto: getInvoiceStatusDisplay,
  review: getReviewStatusDisplay,
  processing: getProcessingStatusDisplay,
};

/** The hover text of the badge `kind` shows, e.g. "Processing status: Successful". */
export function getStatusTooltip(invoice, kind = "auto") {
  const status = (STATUS_DISPLAY[kind] ?? getInvoiceStatusDisplay)(invoice);
  return status.tooltip ?? status.label;
}

function getAppLocale() {
  if (typeof navigator === "undefined") return "en-GB";
  return navigator.language || "en-GB";
}

/**
 * The old app returned "N/A" from `currencyFormatter` whenever the value did
 * not parse, so a missing total never rendered as an amount. Concatenating a
 * symbol onto `Number(null)` produced "£0.00" here instead, which reads as a
 * real zero-value invoice rather than a missing figure.
 *
 * A genuine 0 is still formatted: the old rule was `!Number.parseFloat(value)`,
 * which swallowed real zeros (a zero-rated tax line) along with the nulls.
 */
export function formatCurrency(amount, symbol = "£") {
  const value = typeof amount === "number" ? amount : Number.parseFloat(amount);
  if (!Number.isFinite(value)) return EMPTY_VALUE;

  return `${symbol}${value.toLocaleString(getAppLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatInvoiceDate(isoDate) {
  if (!isoDate) return EMPTY_VALUE;
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return new Intl.DateTimeFormat(getAppLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatInvoiceCount(count) {
  const formatted = Number(count).toLocaleString(getAppLocale());
  return `${formatted} ${count === 1 ? "invoice" : "invoices"}`;
}

/**
 * The API returns some timestamps without a timezone marker, and those are UTC.
 * Reading them as local time shifted every audit entry by the viewer's offset,
 * so the marker is added before parsing.
 */
export function formatDateTime(value) {
  if (!value) return "—";

  const hasTimezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasTimezone ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(getAppLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
